import {
  ChecklistFormValues,
  DepartmentFormValues,
  NetworkNodeFieldPath,
  NetworkNodeFormValues,
  OnboardingUserFormValues,
  RegulationFormValues,
  RoleFormValues,
} from '../../../types';
import {
  BlockActionResult,
  formatUsageScreensMessage,
  getNodeAtPath,
} from '../../networkReferenceUtils';

export type DepartmentUsage = {
  roles: RoleFormValues[];
  usersByDepartment: OnboardingUserFormValues[];
  usersByRole: OnboardingUserFormValues[];
  checklists: ChecklistFormValues[];
  regulations: RegulationFormValues[];
};

const userDepartmentIncludes = (
  departmentId: string[] | 'all',
  deptId: string,
): boolean => Array.isArray(departmentId) && departmentId.includes(deptId);

export function findDepartmentUsage(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
  deptId: string,
): DepartmentUsage {
  const node = getNodeAtPath(network, nodePath);
  const roles = (node?.roles ?? []) as RoleFormValues[];
  const users = (node?.users ?? []) as OnboardingUserFormValues[];
  const checklists = (node?.checklists ?? []) as ChecklistFormValues[];
  const regulations = (node?.regulations ?? []) as RegulationFormValues[];

  const rolesByDept = roles.filter(role => role.departmentId === deptId);
  const roleIdsInDept = new Set(rolesByDept.map(role => role.id));

  const usersByDepartment = users.filter(user =>
    userDepartmentIncludes(user.departmentId, deptId),
  );
  const usersByRole = users.filter(
    user => user.roleId && roleIdsInDept.has(user.roleId),
  );

  const checklistsUsingDept = checklists.filter(
    checklist =>
      !checklist.departments.includes('all') &&
      checklist.departments.includes(deptId),
  );

  const regulationsUsingDept = regulations.filter(
    regulation => regulation.department === deptId,
  );

  return {
    roles: rolesByDept,
    usersByDepartment,
    usersByRole,
    checklists: checklistsUsingDept,
    regulations: regulationsUsingDept,
  };
}

export function isDepartmentInUse(usage: DepartmentUsage): boolean {
  return (
    usage.roles.length > 0 ||
    usage.usersByDepartment.length > 0 ||
    usage.usersByRole.length > 0 ||
    usage.checklists.length > 0 ||
    usage.regulations.length > 0
  );
}

const getDepartmentUsageScreens = (usage: DepartmentUsage): string[] => {
  const screens: string[] = [];

  if (usage.roles.length > 0) {
    screens.push('תפקידים');
  }

  if (usage.usersByDepartment.length > 0 || usage.usersByRole.length > 0) {
    screens.push('משתמשים');
  }

  if (usage.checklists.length > 0) {
    screens.push("צ'קליסטים");
  }

  if (usage.regulations.length > 0) {
    screens.push('רגולציה');
  }

  return screens;
};

export function formatDepartmentDeleteBlockMessage(
  deptName: string,
  usage: DepartmentUsage,
): string {
  const screens = formatUsageScreensMessage(getDepartmentUsageScreens(usage));
  return `לא ניתן למחוק את המחלקה "${deptName || 'ללא שם'}" — היא בשימוש במסכי ${screens}. יש להסיר את השיוכים תחילה`;
}

export function formatDepartmentReplaceBlockMessage(
  inUse: Array<{department: DepartmentFormValues; usage: DepartmentUsage}>,
): string {
  const screens = new Set<string>();
  inUse.forEach(({usage}) => {
    getDepartmentUsageScreens(usage).forEach(screen => screens.add(screen));
  });

  const screensList = formatUsageScreensMessage([...screens]);
  const departmentNames = inUse
    .map(({department}) => `"${department.name?.trim() || 'ללא שם'}"`)
    .join(', ');
  return `לא ניתן לייבא, המחלקות ${departmentNames} בשימוש במסכי ${screensList}. יש להסיר את השיוכים תחילה`;
}

export function getInUseDepartmentsOnNode(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
): Array<{department: DepartmentFormValues; usage: DepartmentUsage}> {
  const node = getNodeAtPath(network, nodePath);
  if (!node) return [];

  return (node.departments ?? [])
    .map(department => ({
      department,
      usage: findDepartmentUsage(network, nodePath, department.id),
    }))
    .filter(({usage}) => isDepartmentInUse(usage));
}

export function canDeleteDepartment(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
  deptId: string,
  deptName: string,
): BlockActionResult {
  const usage = findDepartmentUsage(network, nodePath, deptId);
  if (isDepartmentInUse(usage)) {
    return {
      allowed: false,
      message: formatDepartmentDeleteBlockMessage(deptName, usage),
    };
  }

  return {allowed: true};
}

export function canReplaceDepartments(
  network: NetworkNodeFormValues[],
  nodePath: NetworkNodeFieldPath,
): BlockActionResult {
  const inUse = getInUseDepartmentsOnNode(network, nodePath);

  if (inUse.length === 0) {
    return {allowed: true};
  }

  return {
    allowed: false,
    message: formatDepartmentReplaceBlockMessage(inUse),
  };
}
